// Frontend client for /api/pms/* (provider-agnostic PMS endpoints).
//
// Pairs with the backend `routes/pms.ts` and the provider registry. The
// existing pricing/connection APIs (`pmsConnectionsApi`, `configApi`) still
// own credential CRUD and remain Previo-shaped for one release while we
// migrate UI surfaces to use this client.

import { apiFetch } from "./apiClient";

/**
 * Mirror of `ProviderCapabilities` on the backend. Kept hand-typed to avoid
 * a build-time dependency between the two packages.
 */
export interface PmsProviderCapabilities {
    supportsRestrictionsPush: boolean;
    /**
     * The finest scope at which the PMS stores a minimum stay. Better Hotel
     * keeps one per room type and night. A Previo hotel chooses in Previo's
     * own admin between one per room type and one per night for every room
     * type (`"hotelSetting"`), which the server reads off the PMS
     * (`fetchMinStayScope`) and levels each night for before a send.
     */
    restrictionScope: "property" | "roomType" | "hotelSetting";
    supportsChannelMappedRatePlans: boolean;
    supportsNonRefundableSibling: boolean;
    supportsWebhooks: boolean;
    supportsInventoryPush: boolean;
    supportsAvailabilityRead: boolean;
    /**
     * How long, in minutes, the PMS goes on answering a reading of its price
     * list from a copy it kept. Absent where the read is live, and from a
     * server that does not state it. A restore point of such a PMS may miss a
     * price changed shortly before its upload, which the Upload Log says
     * before a restore is confirmed.
     */
    rateReadCacheMinutes?: number;
    /**
     * A push writes a price for each number of guests; false where the PMS takes one
     * price per room and night (the two-guest one). Absent from a server that does not state it.
     */
    pricesPerGuestCount?: boolean;
}

export type PmsProviderType = "previo" | "better_hotel";

/**
 * Credential schema descriptors — mirror of the backend types in
 * `optivo-service/src/services/pms/providers/types.ts`. Drives the
 * schema-driven wizard / settings form so each provider can declare its
 * own credential fields without UI changes.
 */
export type CredentialFieldType =
    | "text"
    | "email"
    | "password"
    | "url"
    | "number"
    | "select";

/** Selectable option for a `select`-typed credential field. */
export interface CredentialFieldOption {
    value: string;
}

/**
 * Visibility/requiredness condition. A field carrying `showWhen` only applies
 * when the sibling field named `field` currently equals `value`.
 */
export interface CredentialFieldCondition {
    field: string;
    value: string;
}

export interface CredentialFieldDescriptor {
    name: string;
    type: CredentialFieldType;
    required: boolean;
    defaultValue?: string;
    placeholder?: string;
    /** Fixed value the user must not change — rendered read-only. */
    readOnly?: boolean;
    options?: CredentialFieldOption[];
    showWhen?: CredentialFieldCondition;
    i18nKey: string;
}

export interface ProviderCredentialSchema {
    providerType: PmsProviderType;
    fields: CredentialFieldDescriptor[];
}

export interface PmsProviderMetadata {
    providerType: PmsProviderType;
    displayName: string;
    capabilities: PmsProviderCapabilities;
    credentialSchema: ProviderCredentialSchema;
}

/**
 * Look up a provider's credential schema by type. Returns `null` when the
 * provider is unknown to the registry response.
 */
export function getProviderSchema(
    providerType: PmsProviderType,
    providers: PmsProviderMetadata[],
): ProviderCredentialSchema | null {
    return (
        providers.find((p) => p.providerType === providerType)?.credentialSchema ??
        null
    );
}

export async function fetchPmsProviders(): Promise<PmsProviderMetadata[]> {
    const res = await apiFetch("/api/pms/providers");
    if (!res.ok) throw new Error(`Failed to load PMS providers: ${res.status}`);
    const data = await res.json();
    if (!data.success) throw new Error(data.error || "Failed to load PMS providers");
    return data.providers as PmsProviderMetadata[];
}

export interface PmsHealthResult {
    healthy: boolean;
    providerType: PmsProviderType | null;
    error?: string;
}

export async function fetchPmsHealth(): Promise<PmsHealthResult> {
    const res = await apiFetch("/api/pms/health");
    if (!res.ok) throw new Error(`Failed to check PMS health: ${res.status}`);
    const data = await res.json();
    if (!data.success) throw new Error(data.error || "Failed to check PMS health");
    return {
        healthy: !!data.healthy,
        providerType: (data.providerType ?? null) as PmsProviderType | null,
        error: data.error,
    };
}
